import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Heart, Baby as BabyIcon, Camera, Envelope, Notebook, Sparkle,
  Calendar, ChatCircleDots, ArrowRight, BookOpen,
} from "@phosphor-icons/react";
import {
  SunBloom, StageArcs, RingStack, SproutIcon, EnvelopeIcon,
  PolaroidIcon, BookIcon, MilestoneIcon, WeekDots,
  MoodSun, MoodSparkle, MoodMoon, MoodCloud, HorizonLayers,
} from "@/components/vansha/Graphics";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [checkin, setCheckin] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [d, p, c] = await Promise.all([
      api.get("/dashboard"),
      api.get("/prompts/smart"),
      api.get("/checkin/today").catch(() => ({ data: null })),
    ]);
    setData(d.data);
    setPrompts(p.data);
    setCheckin(c.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submitCheckin = async (feeling) => {
    try {
      await api.post("/checkin", { feeling, energy: 3 });
      toast.success("Checked in. Breathe.");
      load();
    } catch { toast.error("Could not save."); }
  };

  if (loading) return (
    <div className="text-ink-500" data-testid="dashboard-loading">
      Gathering today&apos;s stories…
    </div>
  );

  const status = data?.pregnancy_status;
  const week = data?.current_week_content;

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      {/* Hero — colorful, editorial */}
      <section className="relative overflow-hidden vansha-card-tinted-blush p-8 sm:p-10 lg:p-14" data-testid="dashboard-hero">
        <div className="absolute right-0 top-0 opacity-95 pointer-events-none hidden sm:block">
          <SunBloom size={260} />
        </div>
        <div className="absolute left-0 bottom-0 opacity-90 pointer-events-none hidden md:block">
          <HorizonLayers width={360} height={160} />
        </div>
        <div className="relative z-10 max-w-2xl">
          <p className="overline text-terracotta mb-3" data-testid="hero-overline">Today · {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink-900" data-testid="hero-title">
            {greeting()},<br />
            <em className="text-terracotta font-normal italic">{user?.name?.split(" ")[0]}</em>.
          </h1>
          <p className="mt-5 text-ink-700 leading-relaxed max-w-xl">
            {status
              ? `You are in week ${status.current_week}, trimester ${status.trimester}. ${status.days_to_due > 0 ? `${status.days_to_due} days to meet your baby.` : "Your due date has arrived — breathe."}`
              : data?.baby
              ? `${data.baby.name}'s story continues.`
              : "Begin by setting up your family — pregnancy or baby profile."}
          </p>
          {status && (
            <div className="mt-6 flex flex-wrap gap-2" data-testid="hero-chips">
              <span className="chip chip-vermilion">Week {status.current_week}</span>
              <span className="chip chip-marigold">Trimester {status.trimester}</span>
              {status.days_to_due > 0 && <span className="chip chip-sage">{status.days_to_due} days to go</span>}
            </div>
          )}
        </div>
      </section>

      {/* Daily check-in — mood illustrations */}
      {!checkin && (
        <div className="vansha-card p-8" data-testid="dashboard-checkin">
          <p className="overline text-terracotta mb-2">Daily check-in</p>
          <p className="font-serif text-3xl mb-6">How are you feeling today?</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { id: "great", label: "Great", Icon: MoodSun },
              { id: "good", label: "Good", Icon: MoodSparkle },
              { id: "okay", label: "Okay", Icon: MoodMoon },
              { id: "low", label: "Low", Icon: MoodCloud },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => submitCheckin(f.id)}
                className="vansha-card-tinted-sand p-5 flex flex-col items-center gap-3 hover:-translate-y-1 transition-transform"
                data-testid={`checkin-${f.id}`}
              >
                <f.Icon size={56} />
                <span className="font-medium text-ink-900">{f.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Smart prompts */}
      {prompts.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="smart-prompts">
          {prompts.slice(0, 3).map((p, i) => (
            <Link
              to={p.link}
              key={p.id}
              className={`vansha-card p-6 hover:-translate-y-1 transition-transform relative overflow-hidden ${
                i === 0 ? "vansha-card-tinted-sand" : ""
              }`}
              data-testid={`prompt-${p.id}`}
            >
              <div className="absolute -right-4 -top-4 opacity-25 pointer-events-none">
                <RingStack size={100} primary={["#E8552B", "#F4A72C", "#7BA098"][i % 3]} secondary="#F4A72C" />
              </div>
              <div className="relative z-10">
                <p className="overline text-terracotta mb-2">Suggested</p>
                <p className="font-serif text-xl leading-tight">{p.title}</p>
                <p className="text-sm text-ink-700 mt-2">{p.subtitle}</p>
                <p className="text-sm text-terracotta mt-4 font-medium inline-flex items-center gap-1">
                  {p.cta} <ArrowRight size={14} weight="bold" />
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Empty state onboarding CTA */}
      {!data?.pregnancy && !data?.baby && (
        <Link
          to="/app/onboarding"
          className="vansha-card p-8 sm:p-10 block hover:shadow-lg transition relative overflow-hidden"
          data-testid="dashboard-onboard-cta"
        >
          <div className="absolute right-8 -top-4 opacity-90 pointer-events-none hidden md:block">
            <StageArcs width={360} height={180} />
          </div>
          <div className="relative z-10 flex items-center justify-between max-w-md">
            <div>
              <p className="overline text-terracotta mb-2">Begin</p>
              <h2 className="font-serif text-3xl">Set up your family profile</h2>
              <p className="text-ink-700 mt-2">Tell us where you are in the journey — Vansha shapes itself around you.</p>
            </div>
            <ArrowRight weight="thin" size={28} className="text-terracotta" />
          </div>
        </Link>
      )}

      {/* Week feature card — editorial with graphic */}
      {status && week && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Link to="/app/pregnancy" className="block" data-testid="dashboard-week-card">
            <div className="vansha-card p-8 sm:p-10 grid lg:grid-cols-3 gap-8 items-center overflow-hidden relative">
              <div className="lg:col-span-2 relative z-10">
                <p className="overline text-terracotta mb-2">Week {status.current_week} · Trimester {status.trimester}</p>
                <h2 className="font-serif text-3xl sm:text-4xl leading-tight">
                  Your baby is now the size of <em className="text-terracotta font-normal italic">{week.size_comparison}.</em>
                </h2>
                <p className="text-ink-700 mt-3 leading-relaxed">{week.baby_development}</p>
                <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
                  <span className="chip chip-marigold">{week.baby_length_cm} cm</span>
                  <span className="chip chip-vermilion">{week.baby_weight_g} g</span>
                  <span className="chip chip-sage">{status.progress_pct}% along</span>
                </div>
              </div>
              <div className="relative">
                <WeekDots current={status.current_week} total={40} />
                <div className="mt-4 text-center">
                  <p className="font-serif text-6xl text-terracotta leading-none">{status.current_week}</p>
                  <p className="overline mt-2">weeks</p>
                </div>
              </div>
            </div>
          </Link>
        </motion.div>
      )}

      {/* Stat grid with graphics */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="dashboard-grid">
        <StatCard
          Illus={MilestoneIcon}
          label="Milestones"
          count={data?.stats?.milestones || 0}
          to="/app/timeline"
          tid="stat-milestones"
          accent="terracotta"
        />
        <StatCard
          Illus={PolaroidIcon}
          label="Memories"
          count={data?.stats?.memories || 0}
          to="/app/memories"
          tid="stat-memories"
          accent="marigold"
        />
        <StatCard
          Illus={EnvelopeIcon}
          label="Letters"
          count={data?.stats?.letters || 0}
          to="/app/letters"
          tid="stat-letters"
          accent="sage"
        />
        <StatCard
          Illus={BookIcon}
          label="Journal entries"
          count={data?.stats?.journal_entries || 0}
          to="/app/journal"
          tid="stat-journal"
          accent="terracotta"
        />

        {/* Upcoming */}
        <div className="md:col-span-2 lg:col-span-2 vansha-card p-8" data-testid="dashboard-appointments">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="overline text-terracotta mb-1">Upcoming</p>
              <h3 className="font-serif text-2xl">Next moments</h3>
            </div>
            <Link to="/app/appointments" className="text-sm text-terracotta hover:underline font-medium">See all</Link>
          </div>
          {data?.upcoming_appointments?.length ? (
            <ul className="space-y-4">
              {data.upcoming_appointments.slice(0, 3).map((a) => (
                <li key={a.id} className="flex items-start gap-4 text-sm" data-testid={`appt-${a.id}`}>
                  <div className="w-10 h-10 rounded-full bg-vansha-blush flex items-center justify-center flex-shrink-0">
                    <Calendar weight="thin" size={18} className="text-terracotta" />
                  </div>
                  <div className="pt-1.5">
                    <p className="font-medium text-ink-900">{a.title}</p>
                    <p className="text-ink-500">{new Date(a.date_time).toLocaleString()}</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-ink-500 text-sm">No appointments yet. <Link to="/app/appointments" className="text-terracotta hover:underline">Add one</Link>.</p>
          )}
        </div>

        {/* AI assistant CTA */}
        <Link
          to="/app/assistant"
          className="md:col-span-1 lg:col-span-2 bg-forest text-white rounded-2xl p-8 hover:shadow-lg transition relative overflow-hidden"
          data-testid="dashboard-assistant-cta"
        >
          <div className="absolute -right-6 -bottom-6 opacity-70 pointer-events-none">
            <SunBloom size={200} />
          </div>
          <div className="relative z-10">
            <ChatCircleDots weight="thin" size={30} className="text-saffron mb-3" />
            <h3 className="font-serif text-2xl mb-2">Ask Vansha anything</h3>
            <p className="text-sm opacity-85 max-w-xs leading-relaxed">Your personal family concierge — switch between Mother / Father / Sleep / Nutrition / Parenting coach personas.</p>
          </div>
        </Link>

        {/* Yearbook CTA */}
        <Link
          to="/app/yearbook"
          className="md:col-span-2 lg:col-span-2 vansha-card-tinted-sand p-8 hover:-translate-y-1 transition-transform relative overflow-hidden"
          data-testid="dashboard-yearbook"
        >
          <div className="absolute -right-6 -bottom-6 opacity-90 pointer-events-none">
            <BookIcon size={180} />
          </div>
          <div className="relative z-10">
            <p className="overline text-terracotta mb-2">Signature</p>
            <h3 className="font-serif text-2xl mb-2">Your yearbook is taking shape</h3>
            <p className="text-sm text-ink-700 max-w-md leading-relaxed">Auto-generated from every moment you&apos;ve captured. Preview, download as PDF, or order a printed hardcover (Signature).</p>
          </div>
        </Link>

        {/* Modes */}
        <Link to="/app/father" className="vansha-card p-6 hover:-translate-y-1 transition-transform relative overflow-hidden" data-testid="dashboard-father">
          <div className="absolute -right-4 -bottom-4 opacity-70 pointer-events-none">
            <SproutIcon size={100} />
          </div>
          <div className="relative z-10">
            <p className="overline text-terracotta mb-2">Mode</p>
            <h3 className="font-serif text-xl">Father mode</h3>
            <p className="text-sm text-ink-700 mt-2 max-w-[60%]">A separate journey, equally sacred.</p>
          </div>
        </Link>
        <Link to="/app/grandparent" className="vansha-card p-6 hover:-translate-y-1 transition-transform relative overflow-hidden" data-testid="dashboard-grandparent">
          <div className="absolute -right-4 -bottom-4 opacity-70 pointer-events-none">
            <MilestoneIcon size={100} />
          </div>
          <div className="relative z-10">
            <p className="overline text-terracotta mb-2">Mode</p>
            <h3 className="font-serif text-xl">Grandparent mode</h3>
            <p className="text-sm text-ink-700 mt-2 max-w-[60%]">Wisdom, gently passed forward.</p>
          </div>
        </Link>
      </div>

      {/* Recent moments */}
      {data?.recent_timeline?.length > 0 && (
        <section data-testid="dashboard-timeline">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="overline text-terracotta mb-1">Timeline</p>
              <h2 className="font-serif text-3xl">Recent moments</h2>
            </div>
            <Link to="/app/timeline" className="text-sm text-terracotta hover:underline font-medium">View timeline</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.recent_timeline.slice(0, 3).map((t, i) => (
              <div
                key={t.id}
                className={`vansha-card p-6 relative overflow-hidden ${
                  i === 1 ? "vansha-card-tinted-blush" : i === 2 ? "vansha-card-tinted-sand" : ""
                }`}
                data-testid={`timeline-event-${t.id}`}
              >
                <div className="absolute -right-6 -top-6 opacity-25 pointer-events-none">
                  <RingStack size={110} primary={["#E8552B", "#F4A72C", "#7BA098"][i % 3]} secondary="#1B1533" />
                </div>
                <div className="relative z-10">
                  <p className="overline text-terracotta mb-2">
                    {new Date(t.event_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </p>
                  <h3 className="font-serif text-xl leading-tight">{t.title}</h3>
                  {t.description && <p className="text-ink-700 text-sm mt-3 line-clamp-3 leading-relaxed">{t.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

const ACCENT_MAP = {
  terracotta: { text: "text-terracotta", bg: "bg-vansha-blush" },
  marigold:   { text: "text-saffron",    bg: "bg-vansha-sand"  },
  sage:       { text: "text-sage",       bg: "bg-vansha-accent" },
};

function StatCard({ Illus, label, count, to, tid, accent = "terracotta" }) {
  const a = ACCENT_MAP[accent];
  return (
    <Link
      to={to}
      className="vansha-card p-6 hover:-translate-y-1 transition-transform relative overflow-hidden group"
      data-testid={tid}
    >
      <div className={`absolute right-3 top-3 w-12 h-12 rounded-full ${a.bg} flex items-center justify-center`}>
        <Illus size={40} />
      </div>
      <div className="relative z-10 pt-8">
        <p className={`font-serif font-semibold text-5xl ${a.text}`}>{count}</p>
        <p className="overline mt-2">{label}</p>
      </div>
    </Link>
  );
}
